import { getDeliveryApiBase } from "./deliveryApi";
import { getTranslator } from "../i18n";

export function getVideoReviewApiBase() {
    return getDeliveryApiBase();
}

export async function fetchVideoReviewInfo(id) {
    const apiBase = getVideoReviewApiBase();
    if (!apiBase) {
        const error = new Error(getTranslator()("videoReview.api.missingApi"));
        error.code = "MISSING_API";
        throw error;
    }

    const url = `${apiBase}/api/video-review/info/${encodeURIComponent(id)}?_=${Date.now()}`;

    let response;
    try {
        response = await fetch(url, { cache: "no-store" });
    } catch {
        const error = new Error("VIDEO_REVIEW_LOAD_FAILED");
        error.code = "NETWORK_ERROR";
        throw error;
    }

    if (response.status === 404) {
        return null;
    }

    if (response.status === 410) {
        const error = new Error("VIDEO_REVIEW_EXPIRED");
        error.code = "EXPIRED";
        error.status = 410;
        throw error;
    }

    if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const error = new Error(body.error?.message || "VIDEO_REVIEW_LOAD_FAILED");
        error.code = "API_ERROR";
        error.status = response.status;
        throw error;
    }

    const body = await response.json().catch(() => ({}));
    return body.videoReview || null;
}

export async function fetchVideoReviewComments({ linkId, reviewerName }) {
    const apiBase = getVideoReviewApiBase();
    if (!apiBase || !linkId) {
        return [];
    }

    const params = new URLSearchParams({ _: String(Date.now()) });
    if (reviewerName) {
        params.set("reviewerName", reviewerName);
    }

    const url = `${apiBase}/api/video-review/comments/${encodeURIComponent(linkId)}?${params}`;

    let response;
    try {
        response = await fetch(url, { cache: "no-store" });
    } catch {
        return [];
    }

    if (!response.ok) {
        return [];
    }

    const body = await response.json().catch(() => ({}));
    return Array.isArray(body.comments) ? body.comments : [];
}

export async function postVideoReviewComment({ linkId, reviewerName, timestampMs, body }) {
    const apiBase = getVideoReviewApiBase();
    if (!apiBase) {
        const error = new Error(getTranslator()("videoReview.api.missingApi"));
        error.code = "MISSING_API";
        throw error;
    }

    let response;
    try {
        response = await fetch(`${apiBase}/api/video-review/comment`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            cache: "no-store",
            body: JSON.stringify({ linkId, reviewerName, timestampMs, body }),
        });
    } catch {
        const error = new Error("VIDEO_REVIEW_COMMENT_FAILED");
        error.code = "NETWORK_ERROR";
        throw error;
    }

    if (response.status === 410) {
        const error = new Error("VIDEO_REVIEW_EXPIRED");
        error.code = "EXPIRED";
        error.status = 410;
        throw error;
    }

    if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const error = new Error(payload.error?.message || "VIDEO_REVIEW_COMMENT_FAILED");
        error.code = "API_ERROR";
        error.status = response.status;
        throw error;
    }

    const payload = await response.json().catch(() => ({}));
    return payload.comment || null;
}

export async function deleteVideoReviewComment({ commentId, linkId, reviewerName }) {
    const apiBase = getVideoReviewApiBase();
    if (!apiBase) {
        const error = new Error(getTranslator()("videoReview.api.missingApi"));
        error.code = "MISSING_API";
        throw error;
    }

    let response;
    try {
        response = await fetch(`${apiBase}/api/video-review/comment`, {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            cache: "no-store",
            body: JSON.stringify({ commentId, linkId, reviewerName }),
        });
    } catch {
        const error = new Error("VIDEO_REVIEW_COMMENT_DELETE_FAILED");
        error.code = "NETWORK_ERROR";
        throw error;
    }

    if (response.status === 410) {
        const error = new Error("VIDEO_REVIEW_EXPIRED");
        error.code = "EXPIRED";
        error.status = 410;
        throw error;
    }

    if (response.status === 403) {
        const error = new Error("VIDEO_REVIEW_COMMENT_FORBIDDEN");
        error.code = "FORBIDDEN";
        error.status = 403;
        throw error;
    }

    if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const error = new Error(payload.error?.message || "VIDEO_REVIEW_COMMENT_DELETE_FAILED");
        error.code = "API_ERROR";
        error.status = response.status;
        throw error;
    }

    return true;
}
